import { tzOffset } from '@date-fns/tz'
import { computed, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'

import { useCurrentUser } from '@/authuser/helpers.js'
import { useSaveUserMutation } from '@/authuser/mutations.js'
import { useAuthUserQuery } from '@/authuser/queries'
import { useCurrentGroup } from '@/group/helpers.js'
import { defineService } from '@/utils/datastore/helpers'
import { getTimezoneName } from '@/utils/utils.js'

export const useAuthService = defineService(() => {
  // queries
  const {
    user,
    refetch,
    wait: waitForUserToLoad,
    isRefetching,
  } = useAuthUserQuery()
  const { mutate: saveUser } = useSaveUserMutation()

  // computed
  const userId = computed(() => user.value?.id)
  const isLoggedIn = computed(() => Boolean(user.value))

  // Automatically save the users local timezone
  watchEffect(() => {
    if (!user.value) return
    const timezone = getTimezoneName()
    if (timezone && user.value.timezone !== timezone) {
      saveUser({ timezone })
    }
  }, { immediate: true })

  // actions
  async function refresh () {
    if (isRefetching.value) return
    await refetch()
  }

  return {
    userId,
    isLoggedIn,
    user,
    refresh,
    waitForUserToLoad,
  }
})

export const useTimezoneService = defineService(() => {
  const { d } = useI18n()

  const user = useCurrentUser()
  const group = useCurrentGroup()

  const userTimezone = computed(() => user.value?.timezone)
  const groupTimezone = computed(() => group.value?.timezone)

  const inDifferentTimezoneToGroup = computed(() => userTimezone.value && groupTimezone.value && userTimezone.value !== groupTimezone.value)

  function getHasDifferentTimezoneOffset (date) {
    if (!userTimezone.value || !groupTimezone.value) return false
    const userOffset = tzOffset(userTimezone.value, date)
    const groupOffset = tzOffset(groupTimezone.value, date)
    return userOffset !== groupOffset
  }

  function getTimezoneName (date, timezone) {
    return d(date, { key: 'timezone', timeZone: timezone, part: true }).find(part => part.type === 'timeZoneName').value
  }

  return {
    userTimezone,
    groupTimezone,
    getTimezoneName,
    getHasDifferentTimezoneOffset,
    inDifferentTimezoneToGroup,
  }
})
